import React, { useCallback } from 'react';
import { EstimatedTradeCostWidgetSettings } from 'widgets/estimatedTradeCost/widget/components/settings/EstimatedTradeCostWidgetSettings';

import { useEstimatedTradeCostWidgetConfigSlice } from 'widgets/estimatedTradeCost/widget/store/configslice';
import InfoBox from 'utils/components/InfoBox/InfoBox';
import WidgetSettingsButton from 'widgets/WidgetToolbar/WidgetSettingsButton';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useWidgetHasValidSelection } from 'widgets/hooks';
import EstimatedTradeCostToolbar from 'widgets/estimatedTradeCost/widget/components/toolbar/EstimatedTradeCostToolbar';

import './EstimatedTradeCostWidget.scss';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import { EstimatedTradeCostWidgetContent } from 'widgets/estimatedTradeCost/widget/components/EstimatedTradeCostWidgetContent';
import styleConstants from 'styles/variables.module.scss';
import {
    useEditableQuantity,
    useEstimatedTradeCostData,
    useReRunEstimatedTradeCostRequest,
} from 'widgets/estimatedTradeCost/widget/hooks';
import {
    EstimatedTradeCostOrderDataState,
    extractMessages,
} from 'widgets/estimatedTradeCost/store/data/EstimateTradeCostDataState';
import classNames from 'classnames';

const padding = 2 * parseInt(styleConstants.spaceAround);
const toolbarHeight = parseInt(styleConstants.panelHeaderHeight);
const borderWidth = 2 * parseInt(styleConstants.layoutBorderWidth);

export function EstimatedTradeCostWidget() {
    const { widgetKey, height } = useWidgetContext();
    const { config } = useEstimatedTradeCostWidgetConfigSlice();
    const configured = !!(config.benchmark && config.profile);
    const data = useEstimatedTradeCostData();
    const reRun = useReRunEstimatedTradeCostRequest();

    const { editedQty, onQtyChanged, isQuantityDirty, dispatchEditedQuantity } =
        useEditableQuantity(data?.request);

    const { hideToolbar: configHideToolbar } = config;

    const hideToolbar = !editedQty && configHideToolbar;

    useWidgetStream();

    const toolbarConsumedHeight = hideToolbar ? 0 : toolbarHeight;
    const bodyHeight = hideToolbar
        ? height - borderWidth - padding
        : height - toolbarConsumedHeight - borderWidth - padding;

    const onRerunQuery = useCallback(() => {
        dispatchEditedQuantity();
        reRun();
    }, [dispatchEditedQuantity, reRun]);

    const cssClasses = classNames(
        'EstimatedTradeCostWidget',
        (editedQty || data?.request.specifiedQty) && 'edited-qty',
        isQuantityDirty && 'dirty-qty'
    );

    const messages = extractMessages(data);

    return (
        <div className={cssClasses} data-widget-id={widgetKey}>
            <EstimatedTradeCostToolbar
                hideSelectionWarning={!configured}
                mustShow={!!editedQty}
                onRerunQuery={onRerunQuery}
                messages={messages}
                showRefresh={
                    data?.status === 'Completed' ||
                    data?.status === 'Failed' ||
                    !!editedQty
                }
            />
            <div className="widget-body" style={{ height: bodyHeight }}>
                <WidgetBody
                    configured={configured}
                    data={data}
                    editedQty={editedQty}
                    onRerunQuery={onRerunQuery}
                    onQtyChanged={onQtyChanged}
                />
            </div>
        </div>
    );
}

export function WidgetBody({
    configured,
    data,
    editedQty,
    onQtyChanged,
    onRerunQuery,
}: {
    configured: boolean;
    data: EstimatedTradeCostOrderDataState | undefined;
    editedQty: number | undefined;
    onRerunQuery: () => void;
    onQtyChanged: (q: number) => void;
}) {
    const hasValidSelection = useWidgetHasValidSelection();

    if (!configured) {
        return <NotConfiguredWarning />;
    }

    if (!hasValidSelection) {
        return <></>;
    }

    return (
        <EstimatedTradeCostWidgetContent
            data={data}
            editedQty={editedQty}
            onRerunQuery={onRerunQuery}
            onQtyChanged={onQtyChanged}
        />
    );
}

function NotConfiguredWarning() {
    return (
        <InfoBox
            className="configure-box info-box"
            info={
                <>
                    <span>
                        Configure a benchmark and profile in&nbsp;
                        <WidgetSettingsButton
                            header={'Pre-Trade Cost Settings'}
                            triggerText="Widget Settings"
                            body={<EstimatedTradeCostWidgetSettings />}
                            className={'EstimatedTradeCostWidgetSettingsModal'}
                        />
                        .
                    </span>
                </>
            }
        />
    );
}
